/**
 * logger.js - Console logging with production toggle
 *
 * Wrapper for console methods that can be disabled in production.
 * All existing console.log calls remain in code but can be toggled off.
 *
 * Usage:
 * - Set ENABLE_LOGGING = true to enable all console logging
 * - Set ENABLE_LOGGING = false to disable (default for production)
 */

// Toggle flag: set to true to enable logging, false to disable
const ENABLE_LOGGING = false; // Default: OFF for production

// Save original console methods
const originalConsole = {
  log: console.log,
  warn: console.warn,
  error: console.error,
  info: console.info,
  debug: console.debug,
  group: console.group,
  groupCollapsed: console.groupCollapsed,
  groupEnd: console.groupEnd,
};

/**
 * Override console methods based on ENABLE_LOGGING flag
 *
 * When ENABLE_LOGGING is false:
 * - console.log, .info, .debug, .group, .groupCollapsed: no-op
 * - console.warn, .error: still work (critical messages)
 *
 * When ENABLE_LOGGING is true:
 * - All console methods work normally
 */
if (!ENABLE_LOGGING) {
  // Disable non-critical logging
  console.log = () => {};
  console.info = () => {};
  console.debug = () => {};
  console.group = () => {};
  console.groupCollapsed = () => {};
  console.groupEnd = () => {};

  // Keep warn and error for critical issues
  // console.warn and console.error remain functional
}

/**
 * Restore original console methods (for debugging)
 * Call this in browser console to re-enable logging temporarily
 */
export const restoreConsole = () => {
  Object.assign(console, originalConsole);
  console.log('[Logger] Console logging restored');
};

/**
 * Check if logging is enabled
 */
export const isLoggingEnabled = () => ENABLE_LOGGING;

// Log initial state (only if logging is enabled)
if (ENABLE_LOGGING) {
  console.log('[Logger] Console logging is ENABLED');
}
// No startup message when disabled - truly silent production mode

const logger = {
  isEnabled: isLoggingEnabled,
  restore: restoreConsole,
};

export default logger;
